/**
 * Review cards: filter by the selected stage tab, and show a readable first screenful rather than
 * every review at once. The rest are one button away, and switching tabs resets back to the first
 * screenful so a tab never dumps fourteen cards on the page.
 */
class SooidReviews extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.tabs = Array.from(this.querySelectorAll('[data-stage-tab]'));
    this.cards = Array.from(this.querySelectorAll('[data-stage]'));
    this.moreButton = this.querySelector('[data-reviews-more]');
    // Six fills two clean rows of three on a desktop and stays short enough to scan on a phone.
    this.step = Math.max(1, Number(this.dataset.initial) || 6);
    this.limit = this.step;
    this.addEventListener('click', this.onClick.bind(this));
    // Without this the page loads with every stage's cards on screen at once and the selected tab
    // is a lie until someone clicks it.
    const initial = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.stage = initial ? initial.dataset.stageTab : 'all';
    this.render();
  }

  onClick(event) {
    const tab = event.target.closest('[data-stage-tab]');
    if (tab) {
      this.stage = tab.dataset.stageTab;
      this.limit = this.step;
      this.render();
      return;
    }
    if (event.target.closest('[data-reviews-more]')) {
      const total = this.matching().length;
      const collapsing = this.limit >= total;
      this.limit = collapsing ? this.step : total;
      this.render();
      // Collapsing from the bottom of a long list would otherwise leave the viewer stranded
      // below the section.
      if (collapsing) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  matching() {
    return this.cards.filter((card) => this.stage === 'all' || card.dataset.stage === this.stage);
  }

  render() {
    this.tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.stageTab === this.stage)));
    const matches = this.matching();
    const shown = new Set(matches.slice(0, this.limit));
    this.cards.forEach((card) => { card.hidden = !shown.has(card); });

    if (!this.moreButton) return;
    const remaining = matches.length - shown.size;
    this.moreButton.hidden = matches.length <= this.step;
    const moreLabel = (this.dataset.moreLabel || 'Show {n} more reviews').replace('{n}', String(remaining));
    this.moreButton.textContent = remaining > 0
      ? (remaining === 1 ? moreLabel.replace(/reviews$/, 'review') : moreLabel)
      : (this.dataset.fewerLabel || 'Show fewer reviews');
    this.moreButton.setAttribute('aria-expanded', String(remaining === 0));
  }
}

customElements.define('sooid-reviews', SooidReviews);
